import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { EyeOff, HeartPulse } from 'lucide-react';
import { TIRELESS_DIE, rangerWisdomUses } from '../../../shared/rules/ranger.mjs';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import SpellControlsBar from './SpellControlsBar';

const USES = {
  tireless: { name: 'Tireless', stateKey: 'tireless', Icon: HeartPulse, accent: '#7ad7a8', resource: 'action', title: 'Magic action · 1d8 + Wisdom modifier Temporary Hit Points' },
  'natures-veil': { name: 'Nature’s Veil', stateKey: 'naturesVeil', Icon: EyeOff, accent: '#9ad1a0', resource: 'bonusAction', title: 'Bonus Action · Invisible until the end of your next turn' },
};

/**
 * Tireless (Ranger 10) and Nature's Veil (Ranger 14): action tiles with Wisdom-modifier uses per Long Rest. The request
 * is saved before it is sent, so a retry after a dropped connection reuses the same identity and die (like Second Wind).
 */
function RangerActionTile({ useId, form, characterId, campaignId, combatState, isActiveTurn, wisdomModifier, rollDie, onCharacterChange, onCombatStateReconciled }) {
  const config = USES[useId];
  const apiId = String(form?._id || characterId);
  const storageKey = `ranger:${useId}:${campaignId || 'none'}:${apiId}`;
  const [pending, setPending] = useState(null), [busy, setBusy] = useState(false);
  const current = useRef(null), lock = useRef(false);
  const save = value => {
    current.current = value; setPending(value);
    try { if (value) sessionStorage.setItem(storageKey, JSON.stringify(value)); else sessionStorage.removeItem(storageKey); } catch { /* Recovery storage is optional. */ }
  };
  useEffect(() => {
    current.current = null; setPending(null);
    try { const value = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); if (value?.request?.eventId) { current.current = value; setPending(value); } } catch { sessionStorage.removeItem(storageKey); }
  }, [storageKey]);
  const profile = rangerWisdomUses(form, useId, wisdomModifier);
  const inCombat = Number.isInteger(combatState?.activeTurn) && Boolean(combatState?.participants?.[combatState.activeTurn]);
  const ledger = combatState?.turnActionLedger?.combatantId === String(characterId) && combatState.turnActionLedger.turnSequence === combatState.turnSequence ? combatState.turnActionLedger : null;
  const resourceFree = config.resource === 'action' ? !ledger?.actionSpent : !ledger?.bonusActionSpent;
  const available = profile.remaining > 0 && (!inCombat || (isActiveTurn && resourceFree));
  const use = async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true);
    let selected = current.current || { request: { eventId: window.crypto?.randomUUID?.() || `ranger:${useId}:${Date.now()}:${Math.random()}`, turnSequence: inCombat ? combatState.turnSequence : null } };
    try {
      save(selected);
      if (useId === 'tireless' && !selected.request.dice) {
        const dice = await rollDie({ count: 1, sides: TIRELESS_DIE });
        if (!Array.isArray(dice) || dice.length !== 1 || !Number.isInteger(dice[0]) || dice[0] < 1 || dice[0] > TIRELESS_DIE) throw Object.assign(new Error('The d8 did not finish. No use was spent.'), { rejected: true });
        selected = { ...selected, request: { ...selected.request, dice } };
        save(selected);
      }
      const response = await apiFetch(`/characters/${encodeURIComponent(apiId)}/ranger/${useId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(selected.request) });
      const result = await response.json();
      if (!response.ok) throw Object.assign(new Error(result.message || `${config.name} could not be used.`), { rejected: true });
      onCharacterChange?.(previous => (previous ? { ...previous, ...(Number.isFinite(result.temporaryHitPoints) ? { temporaryHitPoints: result.temporaryHitPoints } : {}),
        classState: { ...previous.classState, ranger: { ...previous.classState?.ranger, [config.stateKey]: result.state || previous.classState?.ranger?.[config.stateKey] } } } : previous));
      if (result.combat) onCombatStateReconciled?.(result.combat);
      notify(useId === 'tireless' ? `Tireless · You have ${result.temporaryHitPoints} Temporary Hit Points.` : 'Nature’s Veil · You are Invisible until the end of your next turn.', 'success');
      save(null);
    } catch (error) {
      save({ ...selected, error: error.message, uncertain: !error.rejected });
    } finally { lock.current = false; setBusy(false); }
  };
  if (!profile.available) return null;
  const { Icon, name } = config;
  return <>
    <button type="button" data-action-tile={`ranger:${useId}`} className="combat-hud-resource-tile combat-hud-feature-action-tile" style={{ '--hud-resource-accent': config.accent }}
      title={config.title} aria-label={`${name}: ${profile.remaining} of ${profile.maximum} uses`} disabled={busy || (!pending && !available)} onClick={use}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Icon size={20}/></span>
      <span className="combat-hud-resource-tile__name">{busy ? 'Working…' : pending ? `Retry ${name}` : name}</span>
      <span className="combat-hud-resource-tile__value">{profile.remaining}/{profile.maximum}</span>
    </button>
    {pending?.error && <SpellControlsBar label={name} error={pending.error} hint={pending.uncertain ? 'Retry to confirm the result.' : 'No use was spent.'}
      actions={<><Button size="sm" disabled={busy} onClick={use}>Retry {name}</Button>{!pending.uncertain && <Button size="sm" disabled={busy} onClick={() => save(null)}>Cancel</Button>}</>}/>}
  </>;
}

export default function RangerActionControls(props) {
  return <>{Object.keys(USES).map(useId => <RangerActionTile key={useId} useId={useId} {...props}/>)}</>;
}
